import MomentUtils from "@date-io/moment";
import {
  Box,
  Chip,
  createTheme,
  FormControl,
  FormLabel,
  Grid,
  Paper,
  ThemeProvider,
  Typography,
  useTheme,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { PFXSubItemData } from "../../../../models/Interfaces";
import { DATE_FORMAT } from "../../../../pages/UserForm";
import { selectCleverlynk } from "../../../../redux/slices/cleverlynkSlice";

import { useTranslation } from "react-i18next";
import "../../../../i18n/config";

interface ISubitemDate {
  subitem: PFXSubItemData;
  setChosen: (date: string, id: string) => void;
  textFontFamily: string;
}

const SubitemDate = ({ subitem, setChosen, textFontFamily }: ISubitemDate) => {
  const cleverlynk = useSelector(selectCleverlynk);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [valid, setValid] = useState<boolean>(false);

  const { t } = useTranslation("common");

  const id: string = React.useMemo(() => "date-option-" + Math.floor(Math.random() * 99999), []);
  const theme = useTheme();

  const materialTheme = createTheme({
    palette: {
      primary: { 500: "#fff" },
      text: {
        primary: "#000",
      },
    },
    zIndex: {
      tooltip: 5000,
      modal: 4000,
      snackbar: 4900,
      drawer: 1000,
    },
    overrides: {
      MuiButton: {
        textPrimary: { color: "#000" },
        textSecondary: { color: theme.palette.primary.main },
      },
      MuiInputBase: {
        root: {
          color: theme.palette.secondary.main,
        },
      },
      MuiIconButton: {
        root: {
          color: theme.palette.secondary.main,
        },
      },
      MuiOutlinedInput: {
        root: {
          "&:hover": {
            borderColor: `${theme.palette.secondary.main}aa !important`,
          },
        },
        notchedOutline: {
          borderColor: `${theme.palette.secondary.main} !important`,
          "&:hover": {
            borderColor: `${theme.palette.secondary.main}aa !important`,
          },
        },
      },
    },
  });

  useEffect(() => {
    if (value && valid) {
      setChosen(value, id);
    } else {
      setChosen("", id);
    }
  }, [value, valid]);

  const handleValueChange = (v: string) => {
    const replacedValue = v?.replace(/\/|\_/g, "");
    if (replacedValue.length >= 8) {
      setValue(moment(v, DATE_FORMAT).format(DATE_FORMAT));
      setValid(true);
    } else {
      setValue(v);
      setValid(false);
    }
  };

  const getValue = (v: string) => {
    return v?.replace(/\/|\_/g, "");
  };

  return (
    <Grid item xs={12} key={`date-${subitem.id}`} style={{ margin: "5px 0px" }} className="subitem-render-div">
      <Paper>
        <Box p={2}>
          <FormControl component="fieldset" style={{ width: "100%" }}>
            <FormLabel component="legend" style={{ width: "100%", marginBottom: 5 }}>
              <Grid container justifyContent="flex-start">
                <Typography variant="body1" style={{ fontFamily: textFontFamily }}>
                  {subitem.name}
                </Typography>
                {subitem.mandatory && (
                  <Chip
                    variant="outlined"
                    color="secondary"
                    size="small"
                    label={t("mandatory")}
                    style={{ marginLeft: 15 }}
                  />
                )}
              </Grid>
            </FormLabel>
            <ThemeProvider theme={materialTheme}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={moment.locale("es")}>
                <KeyboardDatePicker
                  placeholder={"DD/MM/AAAA"}
                  inputVariant="outlined"
                  fullWidth
                  required={subitem.mandatory}
                  invalidDateMessage={t("invalidDate")}
                  style={{ color: "#000" }}
                  format={DATE_FORMAT}
                  defaultValue=""
                  value={value ? (getValue(value).length >= 8 ? moment(value, DATE_FORMAT) : value) : undefined}
                  labelFunc={() =>
                    value
                      ? getValue(value).length >= 8
                        ? moment(value, DATE_FORMAT).format("DD [de] MMMM, yyyy")
                        : value
                      : ""
                  }
                  onChange={(_, value) => (value ? handleValueChange(value) : "")}
                />
              </MuiPickersUtilsProvider>
            </ThemeProvider>
          </FormControl>
        </Box>
      </Paper>
    </Grid>
  );
};

export default SubitemDate;
